import { Check } from "lucide-react";
import { SITE_THEMES, type SiteThemeId } from "./siteThemes";

type SiteThemePickerProps = {
  name: string;
  value: SiteThemeId;
  busy?: boolean;
  onChange: (theme: SiteThemeId) => void;
};

/** Radio cards, each with a small page drawn in its own theme. */
export const SiteThemePicker = ({
  name,
  value,
  busy,
  onChange,
}: SiteThemePickerProps) => (
  <fieldset className="space-y-3" aria-busy={busy}>
    <legend className="sr-only">Choose a theme</legend>
    {SITE_THEMES.map((theme) => {
      const selected = value === theme.id;
      return (
        <label
          key={theme.id}
          className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-hazumu-ink ${selected ? "border-hazumu-terracotta bg-hazumu-blush" : "border-hazumu-border bg-hazumu-white hover:border-hazumu-clay"}`}
        >
          <input
            checked={selected}
            className="sr-only"
            name={name}
            onChange={() => onChange(theme.id)}
            type="radio"
            value={theme.id}
          />
          <ThemeSample theme={theme.id} />
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-hazumu-ink">
              {theme.name}
            </span>
            <span className="mt-1 block text-sm leading-5 text-hazumu-muted">
              {theme.description}
            </span>
          </span>
          <Check
            aria-hidden="true"
            size={18}
            className={`shrink-0 text-hazumu-terracotta ${selected ? "" : "invisible"}`}
          />
        </label>
      );
    })}
  </fieldset>
);

const ThemeSample = ({ theme }: { theme: SiteThemeId }) => (
  <span
    aria-hidden="true"
    data-site-theme={theme}
    className="flex w-20 shrink-0 flex-col gap-1.5 self-stretch rounded-xl border border-site-border bg-site-bg p-2.5 text-site-text"
  >
    <span className="site-heading heading-size-20 leading-none">Aa</span>
    <span className="h-1 w-4/5 rounded-full bg-site-muted/45" />
    <span className="h-1 w-1/2 rounded-full bg-site-muted/45" />
    <span className="mt-auto flex gap-1">
      <span className="h-4 flex-1 rounded-[calc(var(--radius-site-sm)/2)] border border-site-border bg-site-surface" />
      <span className="size-4 rounded-[calc(var(--radius-site-sm)/2)] bg-site-accent" />
    </span>
  </span>
);
